import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL;

const articleJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Kakobuy dla początkujących – Jak zacząć i nie popełnić błędów",
  "description": "Zaczynasz przygodę z Kakobuy i nie wiesz, od czego zacząć? Ten poradnik poprowadzi Cię krok po kroku przez cały proces: od rejestracji, przez używanie Kakobuy Spreadsheet, aż po bezpieczną wysyłkę do Polski.",
  "author": {
    "@type": "Organization",
    "name": "Kakobuy Spreadsheet Polska",
    "url": `${SITE_URL}/`
  },
  "publisher": {
    "@type": "Organization",
    "name": "Kakobuy Spreadsheet Polska",
    "logo": {
      "@type": "ImageObject",
      "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png"
    }
  },
  "datePublished": "2026-03-10",
  "dateModified": "2026-07-21",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${SITE_URL}/artykul/kakobuy-dla-poczatkujacych`
  },
  "inLanguage": "pl-PL"
};

export default function PoczatkujacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd} />
      <Navbar />

      <main>
        <article>
          <header className="relative pt-24 pb-16 md:pt-32 md:pb-20 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20desk%20setup%20with%20open%20laptop%20showing%20shopping%20tutorial%20interface%2C%20notebook%20and%20coffee%20cup%20beside%2C%20soft%20morning%20natural%20daylight%2C%20beige%20cream%20Scandinavian%20aesthetic%2C%20cozy%20learning%20atmosphere%2C%20editorial%20product%20photography%2C%20gentle%20shadows%2C%20high%20detail%2C%20organic%20textures%2C%20welcoming%20beginner%20friendly%20workspace&width=1600&height=700&seq=kakobuy-beginner-header-2026b&orientation=landscape"
                alt="Kakobuy poradnik dla początkujących - jak zacząć"
                title="Kakobuy Spreadsheet Polska - poradnik dla początkujących 2026"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            </div>
            <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-semibold">
                  Poradnik
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-semibold">
                  Dla początkujących
                </span>
                <span className="text-xs text-white/70">• 12 min czytania • 2026</span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-6">
                Kakobuy dla początkujących – Jak zacząć i nie popełnić błędów
              </h1>
              <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-3xl">
                Zaczynasz przygodę z Kakobuy i nie wiesz, od czego zacząć? Ten poradnik poprowadzi Cię krok po kroku
                przez cały proces: od rejestracji, przez używanie Kakobuy Spreadsheet, aż po bezpieczną wysyłkę
                do Polski. Wszystko napisane prostym językiem, bez zbędnego żargonu.
              </p>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="prose-custom space-y-10">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  1. Czym jest Kakobuy i dlaczego warto z niego korzystać?
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  <strong>Kakobuy</strong> to agent zakupowy, który umożliwia Polakom kupowanie produktów
                  z chińskich platform zakupowych (takich jak Taobao, Weidian, 1688) bez znajomości
                  języka chińskiego i bez konieczności posiadania chińskiego konta bankowego. Działa to
                  bardzo prosto: Ty wybierasz produkt, Kakobuy kupuje go dla Ciebie, sprawdza jakość,
                  a następnie wysyła do Polski.
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  Z kolei <strong>Kakobuy Spreadsheet</strong> to zorganizowana baza setek produktów,
                  które zostały już sprawdzone i ocenione przez innych użytkowników. To idealne
                  narzędzie dla początkujących — nie musisz samodzielnie przeszukiwać chińskich stron,
                  wszystko masz podane na tacy.
                </p>

                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70 my-6">
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-3">Dlaczego początkujący wybierają Kakobuy:</h3>
                  <ul className="space-y-2">
                    {[
                      'Nie potrzebujesz znać chińskiego — platforma jest po angielsku',
                      'Nie potrzebujesz chińskiego konta bankowego — płacisz przez PayPal lub kartę',
                      'Kakobuy Spreadsheet eliminuje ryzyko — wszystkie linki są sprawdzone',
                      'System QC chroni Cię przed oszustwami — widzisz produkt przed wysyłką',
                      'Wsparcie techniczne pomaga na każdym etapie zamówienia',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                        <span className="w-4 h-4 flex items-center justify-center mt-0.5 text-primary-500">
                          <i className="ri-checkbox-circle-fill text-xs"></i>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  2. Jak zacząć? Instrukcja krok po kroku dla początkujących
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  Rozpoczęcie przygody z <strong>Kakobuy</strong> jest prostsze niż myślisz. Poniżej
                  znajdziesz szczegółową instrukcję, która przeprowadzi Cię przez cały proces
                  — od rejestracji po otrzymanie pierwszej paczki.
                </p>

                <div className="space-y-5 my-6">
                  {[
                    {
                      num: '1',
                      title: 'Załóż konto na Kakobuy',
                      desc: 'Wejdź na stronę Kakobuy i kliknij "Sign Up". Wypełnij formularz rejestracyjny: podaj email, hasło i potwierdź konto przez link aktywacyjny. To zajmuje maksymalnie 3 minuty.',
                    },
                    {
                      num: '2',
                      title: 'Otwórz Kakobuy Spreadsheet',
                      desc: 'Kliknij przycisk "Kakobuy Spreadsheet" na naszej stronie. Zostaniesz przekierowany do arkusza z setkami produktów posegregowanych według kategorii. Przeglądaj, wybieraj, porównuj.',
                    },
                    {
                      num: '3',
                      title: 'Wybierz swój pierwszy produkt',
                      desc: 'Dla początkujących polecamy zacząć od popularnych kategorii: Buty (Shoes), Bluzy (Hoodies) lub Koszulki (T-Shirts). Wybierz produkt z wysoką oceną społeczności.',
                    },
                    {
                      num: '4',
                      title: 'Skopiuj link i wklej w panelu Kakobuy',
                      desc: 'Skopiuj link produktu z arkusza. W panelu Kakobuy wklej go w polu wyszukiwania. System automatycznie rozpozna produkt i pokaże Ci cenę.',
                    },
                    {
                      num: '5',
                      title: 'Złóż zamówienie i opłać',
                      desc: 'Dodaj produkt do koszyka, wybierz rozmiar/kolor (jeśli dotyczy) i opłać zamówienie przez PayPal lub kartę. Pieniądze trafiają do Kakobuy, nie bezpośrednio do sprzedawcy.',
                    },
                    {
                      num: '6',
                      title: 'Poczekaj na zdjęcia QC',
                      desc: 'Po 2-5 dniach produkt dotrze do magazynu Kakobuy. Otrzymasz zdjęcia QC — sprawdź je dokładnie. Jeśli produkt wygląda dobrze, zaakceptuj go.',
                    },
                    {
                      num: '7',
                      title: 'Wybierz wysyłkę do Polski',
                      desc: 'Po akceptacji QC wybierz metodę wysyłki. Dla początkujących polecamy standardową wysyłkę — jest najtańsza i trwa 10-25 dni. Opłać wysyłkę.',
                    },
                    {
                      num: '8',
                      title: 'Odbierz paczkę i ciesz się zakupem!',
                      desc: 'Śledź przesyłkę przez panel Kakobuy. Gdy dotrze do Polski, odbierz ją i sprawdź. Gratulacje — właśnie zrobiłeś swoje pierwsze zakupy przez Kakobuy!',
                    },
                  ].map((item) => (
                    <div key={item.num} className="flex gap-4 p-4 bg-background-100 rounded-xl border border-background-200/70">
                      <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-label font-bold text-sm">
                        {item.num}
                      </span>
                      <div>
                        <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  3. Kakobuy Spreadsheet – Twój najlepszy przyjaciel na start
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  Jeśli jesteś początkujący, <strong>Kakobuy Spreadsheet</strong> to narzędzie, które
                  diametralnie zmieni Twoje doświadczenie zakupowe. Zamiast godzinami przeszukiwać
                  chińskie strony z pomocą translatora, otrzymujesz gotową listę najlepszych produktów.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  {[
                    { title: 'Gotowe linki', desc: 'Nie musisz niczego szukać — wszystkie linki są już w arkuszu. Kliknij i gotowe.', icon: 'ri-link-m' },
                    { title: 'Podział na kategorie', desc: 'Produkty są uporządkowane — Buty, Ubrania, Akcesoria i więcej. Łatwo znajdziesz to, czego szukasz.', icon: 'ri-folder-5-line' },
                    { title: 'Zdjęcia i opisy', desc: 'Każdy produkt ma zdjęcie i krótki opis. Widzisz, co kupujesz, zanim klikniesz.', icon: 'ri-image-line' },
                    { title: 'Aktualizacje', desc: 'Arkusz jest regularnie aktualizowany. Nowe okazje pojawiają się na bieżąco.', icon: 'ri-refresh-line' },
                  ].map((item) => (
                    <div key={item.title} className="p-4 bg-background-100 rounded-xl border border-background-200/70 flex items-start gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className={item.icon}></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  4. Najczęstsze błędy początkujących i jak ich uniknąć
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  Każdy początkujący popełnia błędy — to normalne. Ale lepiej uczyć się na cudzych
                  błędach niż na własnych. Oto lista najczęstszych pułapek, w które wpadają nowi
                  użytkownicy <strong>Kakobuy</strong>, wraz ze wskazówkami, jak ich uniknąć.
                </p>

                <div className="space-y-3 my-6">
                  {[
                    { mistake: 'Kupowanie bez sprawdzenia zdjęć QC', fix: 'Zawsze czekaj na zdjęcia QC przed akceptacją wysyłki. To Twoja jedyna szansa na zwrot produktu bez kosztów.' },
                    { mistake: 'Ignorowanie kosztów wysyłki', fix: 'Sprawdzaj koszt wysyłki przed złożeniem zamówienia. Czasem wysyłka jest droższa niż sam produkt.' },
                    { mistake: 'Brak porównania cen', fix: 'Ten sam produkt może być dostępny u kilku sprzedawców w różnych cenach. Korzystaj z Kakobuy Spreadsheet — tam masz porównanie.' },
                    { mistake: 'Zbyt szybka akceptacja QC', fix: 'Dokładnie analizuj zdjęcia QC. Porównaj je ze zdjęciami producenta. Jeśli coś jest nie tak — zwróć produkt.' },
                    { mistake: 'Zapominanie o cle i VAT', fix: 'Przy zamówieniach powyżej 150 EUR możesz zostać obciążony cłem i VAT. Planuj budżet z uwzględnieniem tych kosztów.' },
                    { mistake: 'Kupowanie od niezweryfikowanych sprzedawców', fix: 'Korzystaj wyłącznie z Kakobuy Spreadsheet. Wszystkie linki w arkuszu są zweryfikowane przez społeczność.' },
                  ].map((item) => (
                    <div key={item.mistake} className="flex items-start gap-3 p-4 bg-secondary-50 rounded-xl border border-secondary-200">
                      <span className="w-6 h-6 flex items-center justify-center mt-0.5 text-secondary-600 flex-shrink-0">
                        <i className="ri-error-warning-line"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">{item.mistake}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.fix}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  5. Ile kosztuje korzystanie z Kakobuy?
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  <strong>Kakobuy</strong> pobiera prowizję za swoje usługi, ale jest ona stosunkowo niska
                  w porównaniu z wartością, jaką oferuje. Oto jak wyglądają koszty:
                </p>

                <div className="overflow-x-auto my-6">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b-2 border-background-300/60">
                        <th className="text-left py-3 px-4 text-sm font-label font-semibold text-foreground-700">Usługa</th>
                        <th className="text-left py-3 px-4 text-sm font-label font-semibold text-foreground-700">Koszt</th>
                        <th className="text-left py-3 px-4 text-sm font-label font-semibold text-foreground-700">Uwagi</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-background-200/70">
                        <td className="py-3 px-4 text-sm text-foreground-700">Prowizja od zakupu</td>
                        <td className="py-3 px-4 text-sm text-foreground-700">~5% wartości zamówienia</td>
                        <td className="py-3 px-4 text-xs text-foreground-500">Wliczone w cenę produktu</td>
                      </tr>
                      <tr className="border-b border-background-200/70">
                        <td className="py-3 px-4 text-sm text-foreground-700">Zdjęcia QC</td>
                        <td className="py-3 px-4 text-sm text-primary-700 font-semibold">Darmowe</td>
                        <td className="py-3 px-4 text-xs text-foreground-500">Wliczone w prowizję</td>
                      </tr>
                      <tr className="border-b border-background-200/70">
                        <td className="py-3 px-4 text-sm text-foreground-700">Wysyłka do Polski</td>
                        <td className="py-3 px-4 text-sm text-foreground-700">Od 50 PLN (mała paczka)</td>
                        <td className="py-3 px-4 text-xs text-foreground-500">Zależy od wagi i metody</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-sm text-foreground-700">Kakobuy Spreadsheet</td>
                        <td className="py-3 px-4 text-sm text-primary-700 font-semibold">Darmowy</td>
                        <td className="py-3 px-4 text-xs text-foreground-500">Dostępny na naszej stronie</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  6. Podsumowanie – czas na Twoje pierwsze zakupy!
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  Teraz, gdy znasz już wszystkie podstawy, jesteś gotowy na swoje pierwsze zakupy
                  przez <strong>Kakobuy</strong>. Pamiętaj: zacznij od małego zamówienia, korzystaj
                  z <strong>Kakobuy Spreadsheet</strong> i zawsze czekaj na zdjęcia QC przed akceptacją wysyłki.
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-6">
                  Tysiące Polaków już korzysta z Kakobuy i oszczędza setki złotych na zakupach.
                  Dołącz do nich i odkryj świat produktów, które normalnie są niedostępne lub
                  znacznie droższe w Polsce. Powodzenia!
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-4 p-6 bg-primary-50 rounded-xl border border-primary-200">
                  <div className="flex-1">
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                      Gotowy na pierwsze zakupy?
                    </h3>
                    <p className="text-sm text-foreground-600">
                      Otwórz Kakobuy Spreadsheet i znajdź swój pierwszy produkt już teraz!
                    </p>
                  </div>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-label font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-rocket-line"></i>
                    Rozpocznij zakupy
                  </a>
                </div>
              </section>

              <nav className="pt-8 border-t border-background-200/70">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-label font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                  Powrót do strony głównej
                </Link>
              </nav>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}